import { resolveClient } from './supabase';
import type { Submission } from '@lingaai/types';
import type { SupabaseClient } from '@supabase/supabase-js';

export async function getSubmissions(portfolioCompanyId?: string, client?: SupabaseClient): Promise<Submission[]> {
  const supabase = resolveClient(client);

  let query = supabase
    .from('submissions')
    .select(
      `
      *,
      portfolio_companies!inner (
        id,
        name,
        sector
      )
    `
    )
    .order('created_at', { ascending: false });

  if (portfolioCompanyId) {
    query = query.eq('portfolio_company_id', portfolioCompanyId);
  }

  const { data, error } = await query;

  if (error) throw error;

  // Fetch metrics for all submissions
  const submissionIds = data.map((s: any) => s.id);
  const metricsMap = new Map<string, any[]>();

  if (submissionIds.length > 0) {
    const { data: metricsData } = await supabase
      .from('metrics')
      .select(
        `
        id,
        submission_id,
        value,
        source_type,
        source_location,
        confidence,
        reviewed,
        metric_definitions!inner (
          id,
          name,
          description
        )
      `
      )
      .in('submission_id', submissionIds);

    // Group metrics by submission
    metricsData?.forEach((metric: any) => {
      const submissionId = metric.submission_id;
      if (!metricsMap.has(submissionId)) {
        metricsMap.set(submissionId, []);
      }
      metricsMap.get(submissionId)!.push(metric);
    });
  }

  // Attach metrics to submissions
  const submissions = data.map((submission: any) => ({
    ...submission,
    metrics: metricsMap.get(submission.id) || [],
  }));

  return submissions as Submission[];
}

export async function getSubmission(submissionId: string, client?: SupabaseClient): Promise<Submission> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('submissions').select('*').eq('id', submissionId).single();

  if (error) throw error;
  return data as Submission;
}

export async function createSubmission(
  data: {
    portfolio_company_id: string;
    period: string;
    source_type: 'upload' | 'manual';
    tenant_id?: string;
    is_trial?: boolean;
  },
  client?: SupabaseClient
): Promise<Submission> {
  const supabase = resolveClient(client);

  const { data: submission, error } = await supabase
    .from('submissions')
    .insert({
      ...data,
      status: 'draft',
    })
    .select()
    .single();

  if (error) throw error;
  return submission as Submission;
}

export async function updateSubmission(submissionId: string, updates: Partial<Submission>, client?: SupabaseClient): Promise<Submission> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase.from('submissions').update(updates).eq('id', submissionId).select().single();

  if (error) throw error;
  return data as Submission;
}

/**
 * Get submission with portfolio company details (join).
 * Used by agents that need company context.
 */
export async function getSubmissionWithCompany(submissionId: string, client?: SupabaseClient): Promise<any> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase
    .from('submissions')
    .select(
      `
      *,
      portfolio_companies (
        id,
        name,
        sector,
        website,
        fund_id
      )
    `
    )
    .eq('id', submissionId)
    .single();

  if (error) throw error;
  return data;
}

/**
 * Delete all submissions for a portfolio company.
 * Cascades to submission_thread and metrics via FK constraints.
 */
export async function deleteSubmissionsByPortCo(portfolioCompanyId: string, client?: SupabaseClient): Promise<void> {
  const supabase = resolveClient(client);

  const { error } = await supabase.from('submissions').delete().eq('portfolio_company_id', portfolioCompanyId);

  if (error) throw error;
}

/**
 * Get submission with all related data (metrics, metric definitions, files, company).
 * Used for submission detail view.
 */
export async function getSubmissionWithDetails(submissionId: string, client?: SupabaseClient): Promise<any> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase
    .from('submissions')
    .select(
      `
      *,
      portfolio_companies!inner (
        id,
        name,
        sector,
        fund_id
      ),
      submission_thread (
        id,
        file_name,
        file_size,
        mime_type,
        file_path
      )
    `
    )
    .eq('id', submissionId)
    .single();

  if (error) throw error;

  // Fetch metrics separately with their definitions
  const { data: metrics, error: metricsError } = await supabase
    .from('metrics')
    .select(
      `
      id,
      metric_definition_id,
      value,
      source_type,
      source_file_id,
      source_location,
      source_coordinates,
      source_page_number,
      confidence,
      extracted_by,
      reviewed,
      reviewed_by,
      reviewed_at,
      metric_definitions!inner (
        id,
        name,
        description,
        display_order
      ),
      metric_comments (
        id,
        comment,
        reply_id,
        created_at,
        updated_at,
        created_by,
        users!metric_comments_created_by_fkey (
          id,
          full_name,
          email
        )
      )
    `
    )
    .eq('submission_id', submissionId)
    .order('metric_definitions(display_order)', { ascending: true });

  if (metricsError) throw metricsError;

  return {
    ...data,
    metrics: metrics || [],
  };
}

/**
 * Get count of submissions, optionally filtered by portfolio company.
 */
export async function getSubmissionsCount(portfolioCompanyId?: string, client?: SupabaseClient): Promise<number> {
  const supabase = resolveClient(client);

  let query = supabase.from('submissions').select('id', { count: 'exact', head: true });

  if (portfolioCompanyId) {
    query = query.eq('portfolio_company_id', portfolioCompanyId);
  }

  const { count, error } = await query;
  if (error) throw error;
  return count || 0;
}

export interface FundSubmissionStatus {
  company_id: string;
  company_name: string;
  submission_id: string | null;
  status: 'draft' | 'submitted' | 'processing' | 'completed' | 'needs_review' | null;
  submitted_at: string | null;
  metrics_count: number;
  metrics?: any[];
  portfolio_companies?: {
    id: string;
    name: string;
  };
  period?: string;
  created_at?: string;
  last_email_sent_at?: string | null;
  email_status?: string | null;
  reminder_count?: number;
}

/**
 * Get submission status for all companies in a fund for a specific period.
 * Returns company details with submission status.
 */
export async function getFundSubmissions(fundId: string, period: string, client?: SupabaseClient): Promise<FundSubmissionStatus[]> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase
    .from('portfolio_companies')
    .select(
      `
      id,
      name,
      submissions!left (
        id,
        status,
        submitted_at,
        period,
        created_at,
        last_email_sent_at,
        email_status,
        reminder_count
      )
    `
    )
    .eq('fund_id', fundId)
    .order('name');

  if (error) throw error;

  // Transform and filter for the specific period
  const results: FundSubmissionStatus[] = [];
  const submissionIds: string[] = [];

  // First pass: collect submission IDs for the period
  for (const company of data) {
    const submission = (company.submissions as any[])?.find((s) => s.period === period);
    if (submission?.id) {
      submissionIds.push(submission.id);
    }
  }

  // Fetch all metrics with definitions for these submissions
  const metricsMap = new Map<string, any[]>();
  if (submissionIds.length > 0) {
    const { data: metricsData } = await supabase
      .from('metrics')
      .select(
        `
        id,
        submission_id,
        value,
        source_type,
        source_location,
        confidence,
        reviewed,
        metric_definitions!inner (
          id,
          name,
          description
        )
      `
      )
      .in('submission_id', submissionIds);

    // Group metrics by submission
    metricsData?.forEach((metric) => {
      const submissionId = metric.submission_id;
      if (!metricsMap.has(submissionId)) {
        metricsMap.set(submissionId, []);
      }
      metricsMap.get(submissionId)!.push(metric);
    });
  }

  // Build results with full submission data including metrics
  for (const company of data) {
    const submission = (company.submissions as any[])?.find((s) => s.period === period);
    const metrics = submission?.id ? metricsMap.get(submission.id) || [] : [];

    results.push({
      company_id: company.id,
      company_name: company.name,
      submission_id: submission?.id || null,
      status: submission?.status || null,
      submitted_at: submission?.submitted_at || null,
      metrics_count: metrics.length,
      metrics: metrics,
      portfolio_companies: {
        id: company.id,
        name: company.name,
      },
      period: submission?.period || period,
      created_at: submission?.created_at,
      last_email_sent_at: submission?.last_email_sent_at,
      email_status: submission?.email_status,
      reminder_count: submission?.reminder_count,
    });
  }

  return results;
}

export interface TenantSubmissionStatus extends FundSubmissionStatus {
  fund_id: string;
  fund_name: string;
}

/**
 * Get all periods that have submissions for a fund.
 * Returns periods in descending order (most recent first).
 */
export async function getFundPeriods(fundId: string, client?: SupabaseClient): Promise<string[]> {
  const supabase = resolveClient(client);

  // Use join approach - query portfolio companies with their submissions
  const { data, error } = await supabase.from('portfolio_companies').select('submissions!inner(period)').eq('fund_id', fundId).not('submissions.period', 'is', null);

  if (error) throw error;

  // Extract unique periods
  const periods = new Set<string>();
  data?.forEach((pc: any) => {
    pc.submissions?.forEach((s: any) => {
      if (s.period) periods.add(s.period);
    });
  });

  // Sort periods chronologically (most recent first)
  return Array.from(periods).sort((a, b) => {
    const [aQuarter, aYear] = a.split(' ');
    const [bQuarter, bYear] = b.split(' ');

    const aYearNum = parseInt(aYear || '0');
    const bYearNum = parseInt(bYear || '0');
    const aQuarterNum = parseInt(aQuarter?.replace('Q', '') || '0');
    const bQuarterNum = parseInt(bQuarter?.replace('Q', '') || '0');

    // Sort by year descending, then quarter descending
    if (aYearNum !== bYearNum) {
      return bYearNum - aYearNum;
    }
    return bQuarterNum - aQuarterNum;
  });
}

/**
 * Get submission status for ALL companies across ALL funds for a specific period.
 * Returns company details with submission status, with fund as a column.
 * Much faster than calling getFundSubmissions multiple times.
 */
export async function getTenantSubmissions(tenantId: string, period: string, client?: SupabaseClient): Promise<TenantSubmissionStatus[]> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase
    .from('portfolio_companies')
    .select(
      `
      id,
      name,
      fund_id,
      funds!inner (
        id,
        name
      ),
      submissions!left (
        id,
        status,
        submitted_at,
        period
      )
    `
    )
    .eq('tenant_id', tenantId)
    .order('name');

  if (error) throw error;

  // Transform and filter for the specific period
  const results: TenantSubmissionStatus[] = [];
  const submissionIds: string[] = [];

  // First pass: collect submission IDs for the period
  for (const company of data) {
    const submission = (company.submissions as any[])?.find((s) => s.period === period);
    if (submission?.id) {
      submissionIds.push(submission.id);
    }
  }

  // Single query to get all metrics counts
  const metricsCounts = new Map<string, number>();
  if (submissionIds.length > 0) {
    const { data: metricsData } = await supabase.from('metrics').select('submission_id').in('submission_id', submissionIds);

    // Count in memory
    metricsData?.forEach((m) => {
      metricsCounts.set(m.submission_id, (metricsCounts.get(m.submission_id) || 0) + 1);
    });
  }

  // Second pass: build results with metrics counts
  for (const company of data) {
    const submission = (company.submissions as any[])?.find((s) => s.period === period);
    const metricsCount = submission?.id ? metricsCounts.get(submission.id) || 0 : 0;
    const fund = company.funds as any;

    results.push({
      company_id: company.id,
      company_name: company.name,
      fund_id: company.fund_id,
      fund_name: fund?.name || 'Unknown Fund',
      submission_id: submission?.id || null,
      status: submission?.status || null,
      submitted_at: submission?.submitted_at || null,
      metrics_count: metricsCount,
    });
  }

  return results;
}

/**
 * Get email timeline for a submission.
 * Returns all email interactions (inbound and outbound) in chronological order.
 */
export async function getSubmissionEmailTimeline(submissionId: string, client?: SupabaseClient): Promise<any[]> {
  const supabase = resolveClient(client);

  const { data, error } = await supabase
    .from('submission_thread')
    .select('id, source_type, email_from, email_to, email_subject, email_received_at, email_sent_at, email_status, created_at, extracted_text')
    .eq('submission_id', submissionId)
    .in('source_type', ['email_body', 'outbound_initial', 'outbound_received', 'outbound_followup', 'outbound_reminder', 'outbound_summary'])
    .order('created_at', { ascending: true });

  if (error) throw error;
  return data || [];
}

/**
 * Send quarterly email to portfolio company
 * Makes API call to backend to create submission and send email
 */
export async function sendQuarterlyEmail(
  portfolioCompanyId: string,
  tenantId: string,
  isTrial: boolean = false
): Promise<{
  success: boolean;
  message: string;
  submission_id: string;
  recipients: string[];
  period: string;
}> {
  // Get API URL from environment
  let apiUrl = 'http://localhost:3000';

  // Try to get from global this (works in browser and Node.js)
  try {
    // @ts-ignore - globalThis is available in modern environments
    if (typeof globalThis !== 'undefined' && globalThis.window?.ENV?.VITE_API_URL) {
      // @ts-ignore
      apiUrl = globalThis.window.ENV.VITE_API_URL;
    } else if (typeof process !== 'undefined' && process.env) {
      apiUrl = process.env.VITE_API_URL || process.env.API_URL || apiUrl;
    }
  } catch {
    // Fall back to default
  }

  const response = await fetch(`${apiUrl}/api/emails/send-quarterly-email`, {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
    },
    credentials: 'include', // Include cookies for auth
    body: JSON.stringify({
      portfolio_company_id: portfolioCompanyId,
      tenant_id: tenantId,
      is_trial: isTrial,
    }),
  });

  if (!response.ok) {
    const error = await response.json() as { error?: string };
    throw new Error(error.error || 'Failed to send quarterly email');
  }

  const result = await response.json() as {
    success: boolean;
    message: string;
    submission_id: string;
    recipients: string[];
    period: string;
  };

  return result;
}
